import { NavLink, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import SupportNoticeModal from "@/components/SupportNoticeModal";
import {
  LayoutDashboard,
  FileText,
  Boxes,
  Users,
  UserCog,
  BarChart3,
  UsersRound,
  Settings as SettingsIcon,
  LifeBuoy,
  LogOut,
  Zap,
} from "lucide-react";

const NAV = [
  { to: "/", label: "Tableau de Bord", icon: LayoutDashboard, testid: "sidebar-dashboard", end: true },
  { to: "/facturation", label: "Facturation & Devis", icon: FileText, testid: "sidebar-facturation", mod: "facturation" },
  { to: "/inventaire", label: "Inventaire & Stocks", icon: Boxes, testid: "sidebar-inventaire", mod: "inventaire" },
  { to: "/crm", label: "CRM & Prospects", icon: Users, testid: "sidebar-crm", mod: "crm" },
  { to: "/rh", label: "Ressources Humaines", icon: UserCog, testid: "sidebar-rh", mod: "rh" },
  { to: "/rapports", label: "Rapports", icon: BarChart3, testid: "sidebar-rapports", mod: "rapports" },
  { to: "/equipe", label: "Équipe", icon: UsersRound, testid: "sidebar-equipe", ownerOnly: true },
  { to: "/parametres", label: "Paramètres", icon: SettingsIcon, testid: "sidebar-parametres" },
  { to: "/support", label: "Support", icon: LifeBuoy, testid: "sidebar-support" },
];

const TITLES = {
  "/": "Tableau de Bord",
  "/facturation": "Facturation & Devis",
  "/inventaire": "Inventaire & Stocks",
  "/crm": "CRM & Prospects",
  "/rh": "Ressources Humaines",
  "/rapports": "Rapports Avancés",
  "/parametres": "Paramètres",
  "/support": "Support",
};

export default function Layout() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const title = TITLES[location.pathname] || "GestionPro";
  const nav = NAV.filter((n) => {
    if (n.ownerOnly && user?.role !== "owner") return false;
    if (n.mod && user?.role !== "owner" && !(user?.permissions || []).includes(n.mod)) return false;
    return true;
  });
  const company = user?.company;
  const showTrial = company?.subscription_status === "trialing" && company?.trial_days_left != null;

  return (
    <div className="flex min-h-screen bg-background text-foreground">
      <SupportNoticeModal />
      {/* Sidebar */}
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-64 flex-col border-r border-border bg-card grid-noise md:flex">
        <div className="flex items-center gap-2 border-b border-border px-6 py-5">
          <div className="flex h-8 w-8 items-center justify-center bg-primary text-primary-foreground">
            <Zap size={18} strokeWidth={2.5} />
          </div>
          <span className="font-display text-lg font-bold tracking-tight">GestionPro</span>
        </div>
        {company?.name && (
          <div className="border-b border-border px-6 py-3">
            <p className="truncate text-xs uppercase tracking-wide text-muted-foreground">Entreprise</p>
            <p className="truncate text-sm font-medium" data-testid="company-name">{company.name}</p>
          </div>
        )}

        <nav className="flex-1 space-y-1 px-3 py-4">
          {nav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={item.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 text-sm transition-colors duration-200 active:scale-[0.98] ${
                  isActive
                    ? "bg-primary/10 text-primary border-l-2 border-primary font-medium"
                    : "text-muted-foreground hover:bg-accent hover:text-foreground border-l-2 border-transparent"
                }`
              }
            >
              <item.icon size={18} />
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="border-t border-border p-4">
          <div className="mb-3 flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center bg-secondary font-mono text-sm text-primary">
              {(user?.name || user?.email || "?").slice(0, 2).toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium">{user?.name}</p>
              <p className="truncate text-xs uppercase tracking-wide text-muted-foreground">
                {user?.role === "owner" ? "Responsable" : "Employé"}
              </p>
            </div>
          </div>
          <button
            onClick={logout}
            data-testid="logout-btn"
            className="flex w-full items-center justify-center gap-2 border border-border bg-transparent px-3 py-2 text-sm text-muted-foreground transition-colors duration-200 hover:border-destructive hover:text-destructive"
          >
            <LogOut size={16} /> Déconnexion
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex flex-1 flex-col md:ml-64">
        <header className="sticky top-0 z-20 flex items-center justify-between border-b border-border bg-background/80 px-6 py-4 backdrop-blur">
          <h1 className="font-display text-2xl font-bold tracking-tight" data-testid="page-title">
            {title}
          </h1>
          <div className="hidden items-center gap-2 font-mono text-xs text-muted-foreground sm:flex">
            {new Date().toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" })}
          </div>
        </header>
        <main className="flex-1 p-6">
          {showTrial && (
            <div className="mb-6 flex flex-wrap items-center justify-between gap-3 border border-primary/30 bg-primary/10 px-4 py-3" data-testid="trial-banner">
              <p className="text-sm text-primary">
                Essai gratuit — <strong>{company.trial_days_left} jour(s)</strong> restant(s). Passez à 100€/mois pour ne pas être interrompu.
              </p>
              {user?.role === "owner" && (
                <a href="/billing" data-testid="trial-subscribe-link" className="bg-primary px-4 py-1.5 text-xs font-semibold text-primary-foreground transition-colors hover:bg-primary/90">
                  S'abonner
                </a>
              )}
            </div>
          )}
          <Outlet />
        </main>
      </div>
    </div>
  );
}
